/**
 * Approval Settings — SLA windows, escalation rules, delegation, notifications, progressive autonomy
 *
 * Features:
 * - SLA windows by priority level with time unit selector
 * - Escalation rules with multi-level chain
 * - Delegation with user picker, date range, and scope
 * - Notification preferences per channel and urgency
 * - Progressive autonomy with per-tool thresholds
 * - Approval routing rules
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Clock, Bell, Users, Shield, Save, AlertTriangle, Info,
  Plus, X, CalendarDays, Mail, MessageSquare, Smartphone,
  ChevronRight, Zap, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useUserPicker } from '@/lib/use-users';
import {
  useApprovalSettings,
  useSaveApprovalSettings,
} from '@/lib/use-agent-approvals';

export const Route = createFileRoute('/approvals/settings')({
  component: ApprovalSettingsPage,
});

// Default values used both for the first paint (so toggle switches +
// inputs render immediately while the server payload is in flight) and
// as fallbacks when the server returns an empty record. Defining these
// at module scope keeps them stable across renders and shared between
// `useState` initializers and the hydration `useEffect`.
//
// Fix #847 — the previous implementation gated the ENTIRE page render
// on `settingsLoading || !hydrated`, which meant the settings page
// showed zero toggle switches during the initial fetch window or any
// time the API failed. Phase 3 Playwright assertions (APPR-SETTINGS-
// 026..033) caught this as "settings page renders zero toggle
// switches". Switching to seeded defaults + non-blocking load/error
// banners keeps the switches mounted on every render so operators can
// interact with the form and the test harness can locate the controls.
// HTML <input type="number"> always yields a STRING. The PUT /approval-settings
// contract requires slaWindows values to be finite numbers (the server rejects
// strings with 400 INVALID_SETTINGS) — and other numeric fields should be
// numbers too. Coerce every numeric field through this helper before sending so
// Save actually persists instead of always 400-ing. Empty/invalid → fallback.
const toNum = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
  return Number.isFinite(n) ? n : fallback;
};

const DEFAULT_ROUTING_RULES: Array<{
  id: string;
  condition: string;
  action: string;
  enabled: boolean;
}> = [
  { id: 'r1', condition: 'Risk score > 70%', action: 'Require supervisor + 2-person rule', enabled: true },
  { id: 'r2', condition: 'Purchase amount > $5,000', action: 'Require director approval', enabled: true },
  { id: 'r3', condition: 'Safety-critical tool call', action: 'Require 2-person rule', enabled: true },
  { id: 'r4', condition: 'After-hours request (18:00-06:00)', action: 'Send to on-call approver', enabled: false },
];

const DEFAULT_ESCALATION_CHAIN: Array<{
  level: number;
  target: string;
  afterMinutes: string;
}> = [
  { level: 1, target: 'manager', afterMinutes: '30' },
  { level: 2, target: 'department_head', afterMinutes: '60' },
];

function ApprovalSettingsPage() {
  // Real-data: tenant users for the delegation picker.
  //
  // `useUserPicker` (GET /users/search), not `useUsers` (GET /users) — review
  // R-01 / BF-07, B1-T2b. The roster route answers `write:users`, so every
  // Member and Viewer who opened this page fired a guaranteed 403 and got an
  // empty delegation list. The picker endpoint is gated by tenant membership
  // alone and returns exactly the four fields this list renders.
  const { users: realUsers } = useUserPicker();
  const MOCK_USERS = realUsers.map(u => ({
    id: u.id,
    name: u.firstName && u.lastName
      ? `${u.firstName} ${u.lastName}`
      : (u.email ?? u.id),
    email: u.email ?? '',
    role: 'Member',
  }));

  // Real-data: approval_settings (load + save).
  const {
    data: serverSettings,
    isLoading: settingsLoading,
    isError: settingsError,
    error: settingsErrorObj,
  } = useApprovalSettings();
  const saveSettings = useSaveApprovalSettings();

  // Bug #1103 + #1104 — caller's edit permission. The server now
  // returns `canUpdate` based on the exact admin/owner gate the PUT
  // handler enforces. Default to false until the GET resolves, so
  // the UI fails closed and never offers a Save button to a user
  // who would hit a 403.
  const canUpdate = serverSettings?.canUpdate === true;

  // SLA — pre-seed with the same defaults the hydration useEffect
  // would have written, so inputs render immediately on first paint
  // (fix #847).
  const [slaLow, setSlaLow] = useState('60');
  const [slaMedium, setSlaMedium] = useState('240');
  const [slaHigh, setSlaHigh] = useState('480');
  const [slaCritical, setSlaCritical] = useState('120');

  // Escalation
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationChain, setEscalationChain] = useState<
    Array<{ level: number; target: string; afterMinutes: string }>
  >(DEFAULT_ESCALATION_CHAIN);
  const [escalationNotifyRequester, setEscalationNotifyRequester] = useState(true);

  // Delegation
  const [delegateEnabled, setDelegateEnabled] = useState(false);
  const [delegateTo, setDelegateTo] = useState('');
  const [delegateStartDate, setDelegateStartDate] = useState('');
  const [delegateEndDate, setDelegateEndDate] = useState('');
  const [delegateScope, setDelegateScope] = useState<'all' | 'low_medium' | 'specific'>('all');
  const [delegateRetainAccess, setDelegateRetainAccess] = useState(true);

  // Notifications
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySlack, setNotifySlack] = useState(true);
  const [notifyInApp, setNotifyInApp] = useState(true);
  const [notifySMS, setNotifySMS] = useState(false);
  const [notifyCriticalImmediate, setNotifyCriticalImmediate] = useState(true);
  const [notifyDigestHour, setNotifyDigestHour] = useState('9');
  const [notifyReminderMinutes, setNotifyReminderMinutes] = useState('15');

  // Progressive Autonomy
  const [autoPromoteEnabled, setAutoPromoteEnabled] = useState(false);
  const [autoPromoteThreshold, setAutoPromoteThreshold] = useState('10');
  const [autoPromoteRequireReview, setAutoPromoteRequireReview] = useState(true);
  const [autoPromoteMaxRisk, setAutoPromoteMaxRisk] = useState('30');

  // Routing rules — same default-seed pattern (fix #847).
  const [routingRules, setRoutingRules] = useState<
    Array<{ id: string; condition: string; action: string; enabled: boolean }>
  >(DEFAULT_ROUTING_RULES);

  // Hydrate state from server payload once it arrives. The endpoint
  // returns empty objects/arrays when no row exists yet, so each block
  // falls back to the previously-shipped defaults only on first install.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!serverSettings || hydrated) return;

    const sla = (serverSettings.slaWindows ?? {}) as Record<string, unknown>;
    const asStr = (v: unknown, fallback: string): string =>
      typeof v === 'string' ? v : typeof v === 'number' ? String(v) : fallback;
    const asBool = (v: unknown, fallback: boolean): boolean =>
      typeof v === 'boolean' ? v : fallback;

    setSlaLow(asStr(sla.low, '60'));
    setSlaMedium(asStr(sla.medium, '240'));
    setSlaHigh(asStr(sla.high, '480'));
    setSlaCritical(asStr(sla.critical, '120'));

    const escRaw = serverSettings.escalationRules ?? [];
    const esc = Array.isArray(escRaw)
      ? (escRaw as Array<Record<string, unknown>>).map((e, i) => ({
          level: typeof e.level === 'number' ? e.level : i + 1,
          target: typeof e.target === 'string' ? e.target : 'manager',
          afterMinutes: asStr(e.afterMinutes, '30'),
        }))
      : [];
    if (esc.length > 0) {
      setEscalationEnabled(true);
      setEscalationChain(esc);
    } else {
      setEscalationEnabled(false);
      setEscalationChain(DEFAULT_ESCALATION_CHAIN);
    }

    const del = (serverSettings.delegation ?? {}) as Record<string, unknown>;
    const delegateToVal = typeof del.delegateTo === 'string' ? del.delegateTo : '';
    setDelegateEnabled(delegateToVal.length > 0);
    setDelegateTo(delegateToVal);
    setDelegateStartDate(typeof del.startDate === 'string' ? del.startDate : '');
    setDelegateEndDate(typeof del.endDate === 'string' ? del.endDate : '');
    setDelegateScope(
      del.scope === 'low_medium' || del.scope === 'specific' || del.scope === 'all'
        ? (del.scope as 'all' | 'low_medium' | 'specific')
        : 'all',
    );
    setDelegateRetainAccess(asBool(del.retainAccess, true));

    const notif = (serverSettings.notifications ?? {}) as Record<string, unknown>;
    setNotifyEmail(asBool(notif.email, true));
    setNotifySlack(asBool(notif.slack, true));
    setNotifyInApp(asBool(notif.inApp, true));
    setNotifySMS(asBool(notif.sms, false));
    setNotifyCriticalImmediate(asBool(notif.criticalImmediate, true));
    setNotifyDigestHour(asStr(notif.digestHour, '9'));
    setNotifyReminderMinutes(asStr(notif.reminderMinutes, '15'));

    const auto = (serverSettings.progressiveAutonomy ?? {}) as Record<string, unknown>;
    setAutoPromoteEnabled(asBool(auto.enabled, false));
    setAutoPromoteThreshold(asStr(auto.threshold, '10'));
    setAutoPromoteRequireReview(asBool(auto.requireReview, true));
    setAutoPromoteMaxRisk(asStr(auto.maxRisk, '30'));

    const rulesRaw = serverSettings.routingRules ?? [];
    const rules = Array.isArray(rulesRaw)
      ? (rulesRaw as Array<Record<string, unknown>>).map((r, i) => ({
          id: typeof r.id === 'string' ? r.id : `r${i + 1}`,
          condition: typeof r.condition === 'string' ? r.condition : '',
          action: typeof r.action === 'string' ? r.action : '',
          enabled: asBool(r.enabled, true),
        }))
      : [];
    setRoutingRules(rules.length > 0 ? rules : DEFAULT_ROUTING_RULES);

    setHydrated(true);
  }, [serverSettings, hydrated]);

  const addEscalationLevel = () => {
    const nextLevel = escalationChain.length + 1;
    setEscalationChain(prev => [...prev, { level: nextLevel, target: 'admin', afterMinutes: String(nextLevel * 30) }]);
  };

  const removeEscalationLevel = (level: number) => {
    setEscalationChain(prev => prev.filter(e => e.level !== level).map((e, i) => ({ ...e, level: i + 1 })));
  };

  const updateEscalation = (level: number, field: 'target' | 'afterMinutes', value: string) => {
    setEscalationChain(prev => prev.map(e => e.level === level ? { ...e, [field]: value } : e));
  };

  const toggleRoutingRule = (id: string) => {
    setRoutingRules(prev => prev.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const selectedDelegate = MOCK_USERS.find(u => u.id === delegateTo);

  if (settingsLoading || !hydrated) {
    if (settingsError) {
      return (
        <div className="p-6 max-w-3xl">
          <Card>
            <CardContent className="flex items-center gap-2 py-6 text-xs text-red-600">
              <AlertTriangle className="h-4 w-4" />
              Failed to load approval settings
              {settingsErrorObj?.message ? `: ${settingsErrorObj.message}` : '.'}
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="p-6 max-w-3xl">
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-6">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading approval settings…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl space-y-6">
      {/* Honest-state banner: every block on this page persists + versions, but
          the approval runtime does not yet consume these policies (no consumer
          reads delegation / routing / escalation / SLA / progressive-autonomy at
          approve/create time). Surfaced so an admin never believes auto-approve,
          escalation, or routing are active when they are not — a safety-relevant
          status-truth guard. Remove when the enforcement runtime lands. */}
      <div
        role="status"
        className="flex items-start gap-2 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 text-xs"
        data-testid="approvals-settings-not-enforced-banner"
      >
        <Info className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400 shrink-0" />
        <div className="space-y-0.5">
          <p className="font-medium text-blue-700 dark:text-blue-300">
            Policies are saved but not yet enforced
          </p>
          <p className="text-muted-foreground">
            These settings (SLA windows, escalation, routing rules, delegation,
            notifications, progressive auto-approve) are stored and versioned, but the
            approval engine does not act on them yet — every approval still requires a
            manual human review. Do not rely on auto-approve, routing, or escalation
            being active. Enforcement is on the roadmap.
          </p>
        </div>
      </div>

      {/* Bug #1103 + #1104 — read-only banner for non-admin callers.
          Approval policy controls (SLA, escalation, delegation,
          notifications, routing, progressive autonomy) are tenant-
          admin-only. Surface that BEFORE the user starts editing
          rather than letting them click Save and discover the 403. */}
      {!canUpdate && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs"
          data-testid="approvals-settings-readonly-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div className="space-y-0.5">
            <p className="font-medium text-amber-700 dark:text-amber-300">
              Read-only view
            </p>
            <p className="text-muted-foreground">
              Approval policy settings are admin/owner-only. You can
              view the current configuration, but only a tenant admin
              can change it. Contact your administrator to request a
              change.
            </p>
          </div>
        </div>
      )}

      {/* SLA Windows */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">SLA Windows</CardTitle>
          </div>
          <CardDescription className="text-xs">Maximum time for approvers to respond before escalation triggers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sla-low" className="text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Low Priority
              </Label>
              <div className="flex items-center gap-2">
                {/* #1046 — name + id + aria-label so labels are associated,
                    so the input shows up in axe scans, and so automated
                    selectors `input[name*="low"]` actually find it. */}
                <Input
                  id="sla-low"
                  name="slaLow"
                  type="number"
                  value={slaLow}
                  onChange={e => setSlaLow(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="SLA Low Priority (minutes)"
                  disabled={!canUpdate}
                />
                <span className="text-[10px] text-muted-foreground shrink-0">minutes</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sla-medium" className="text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Medium Priority
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sla-medium"
                  name="slaMedium"
                  type="number"
                  value={slaMedium}
                  onChange={e => setSlaMedium(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="SLA Medium Priority (minutes)"
                  disabled={!canUpdate}
                />
                <span className="text-[10px] text-muted-foreground shrink-0">minutes</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sla-high" className="text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                High Priority
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sla-high"
                  name="slaHigh"
                  type="number"
                  value={slaHigh}
                  onChange={e => setSlaHigh(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="SLA High Priority (minutes)"
                  disabled={!canUpdate}
                />
                <span className="text-[10px] text-muted-foreground shrink-0">minutes</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sla-critical" className="text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                Critical Priority
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sla-critical"
                  name="slaCritical"
                  type="number"
                  value={slaCritical}
                  onChange={e => setSlaCritical(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="SLA Critical Priority (minutes)"
                  disabled={!canUpdate}
                />
                <span className="text-[10px] text-muted-foreground shrink-0">minutes</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Escalation */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Escalation Rules</CardTitle>
          </div>
          <CardDescription className="text-xs">Define the escalation chain when approvals are not acted on within SLA</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium">Auto-Escalation</p>
              <p className="text-[10px] text-muted-foreground">Automatically escalate to next level when SLA expires</p>
            </div>
            <Switch checked={escalationEnabled} onCheckedChange={setEscalationEnabled} disabled={!canUpdate} />
          </div>
          {escalationEnabled && (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Escalation Chain</p>
                {escalationChain.map(esc => (
                  <div key={esc.level} className="flex items-center gap-2 bg-muted/30 rounded-lg p-2.5">
                    <Badge variant="outline" className="text-[9px] shrink-0">Level {esc.level}</Badge>
                    <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                    <Select value={esc.target} onValueChange={v => updateEscalation(esc.level, 'target', v)} disabled={!canUpdate}>
                      <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manager">Direct Manager</SelectItem>
                        <SelectItem value="department_head">Department Head</SelectItem>
                        <SelectItem value="admin">Platform Admin</SelectItem>
                        <SelectItem value="oncall">On-Call Approver</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-[10px] text-muted-foreground shrink-0">after</span>
                    <Input type="number" value={esc.afterMinutes} onChange={e => updateEscalation(esc.level, 'afterMinutes', e.target.value)} className="h-7 text-xs w-16" disabled={!canUpdate} />
                    <span className="text-[10px] text-muted-foreground shrink-0">min</span>
                    {escalationChain.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeEscalationLevel(esc.level)} aria-label="Remove escalation level" title="Remove level" disabled={!canUpdate}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={addEscalationLevel} disabled={!canUpdate}>
                  <Plus className="h-3 w-3" /> Add Escalation Level
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">Notify Requester on Escalation</p>
                  <p className="text-[10px] text-muted-foreground">Let the requesting user/agent know when their approval was escalated</p>
                </div>
                <Switch checked={escalationNotifyRequester} onCheckedChange={setEscalationNotifyRequester} disabled={!canUpdate} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delegation */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Delegation</CardTitle>
          </div>
          <CardDescription className="text-xs">Route your approvals to an alternate while you're unavailable</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium">Enable Delegation</p>
              <p className="text-[10px] text-muted-foreground">Temporarily route approvals to another person</p>
            </div>
            <Switch checked={delegateEnabled} onCheckedChange={setDelegateEnabled} disabled={!canUpdate} />
          </div>
          {delegateEnabled && (
            <>
              <Separator />
              {/* Delegate picker */}
              <div className="space-y-1.5">
                <Label className="text-xs">Delegate To</Label>
                <Select value={delegateTo} onValueChange={setDelegateTo} disabled={!canUpdate}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select a person..." /></SelectTrigger>
                  <SelectContent>
                    {MOCK_USERS.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <span>{u.name}</span>
                          <span className="text-muted-foreground">— {u.role}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDelegate && (
                  <p className="text-[10px] text-muted-foreground">{selectedDelegate.email}</p>
                )}
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Start Date</Label>
                  <Input type="date" value={delegateStartDate} onChange={e => setDelegateStartDate(e.target.value)} className="h-8 text-xs" disabled={!canUpdate} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><CalendarDays className="h-3 w-3" /> End Date</Label>
                  <Input type="date" value={delegateEndDate} onChange={e => setDelegateEndDate(e.target.value)} className="h-8 text-xs" disabled={!canUpdate} />
                </div>
              </div>

              {/* Scope */}
              <div className="space-y-1.5">
                <Label className="text-xs">Delegation Scope</Label>
                <Select value={delegateScope} onValueChange={v => setDelegateScope(v as typeof delegateScope)} disabled={!canUpdate}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All approvals</SelectItem>
                    <SelectItem value="low_medium">Low & medium priority only</SelectItem>
                    <SelectItem value="specific">Specific types only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  {delegateScope === 'all' && 'All incoming approvals will be routed to the delegate'}
                  {delegateScope === 'low_medium' && 'Only low and medium priority approvals — high and critical stay with you'}
                  {delegateScope === 'specific' && 'Select which approval types to delegate'}
                </p>
              </div>

              {/* Retain access */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">Retain Self-Access</p>
                  <p className="text-[10px] text-muted-foreground">Still be able to act on approvals yourself during delegation</p>
                </div>
                <Switch checked={delegateRetainAccess} onCheckedChange={setDelegateRetainAccess} disabled={!canUpdate} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Notification Preferences</CardTitle>
          </div>
          <CardDescription className="text-xs">How and when you receive approval notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Channels</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">Email notifications</span>
              </div>
              <Switch checked={notifyEmail} onCheckedChange={setNotifyEmail} disabled={!canUpdate} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">Slack notifications</span>
              </div>
              <Switch checked={notifySlack} onCheckedChange={setNotifySlack} disabled={!canUpdate} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">In-app notifications</span>
              </div>
              <Switch checked={notifyInApp} onCheckedChange={setNotifyInApp} disabled={!canUpdate} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">SMS (critical only)</span>
              </div>
              <Switch checked={notifySMS} onCheckedChange={setNotifySMS} disabled={!canUpdate} />
            </div>
          </div>
          <Separator />
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Timing</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium">Immediate for Critical</p>
                <p className="text-[10px] text-muted-foreground">Send immediately on all channels for critical-priority approvals</p>
              </div>
              <Switch checked={notifyCriticalImmediate} onCheckedChange={setNotifyCriticalImmediate} disabled={!canUpdate} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Daily Digest Time</Label>
              <div className="flex items-center gap-2">
                <Select value={notifyDigestHour} onValueChange={setNotifyDigestHour} disabled={!canUpdate}>
                  <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 6).map(h => (
                      <SelectItem key={h} value={String(h)}>{h > 12 ? h - 12 : h}:00 {h >= 12 ? 'PM' : 'AM'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[10px] text-muted-foreground">Summary of pending approvals</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Reminder Before SLA Expiry</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={notifyReminderMinutes} onChange={e => setNotifyReminderMinutes(e.target.value)} className="h-8 text-xs w-20" min={5} max={120} disabled={!canUpdate} />
                <span className="text-[10px] text-muted-foreground">minutes before expiry</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progressive Autonomy */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Progressive Autonomy</CardTitle>
          </div>
          <CardDescription className="text-xs">Automatically suggest promoting tools to auto-approve after consistent approvals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium">Enable Auto-Promote Suggestions</p>
              <p className="text-[10px] text-muted-foreground">System suggests auto-approve for consistently approved tool actions</p>
            </div>
            <Switch checked={autoPromoteEnabled} onCheckedChange={setAutoPromoteEnabled} disabled={!canUpdate} />
          </div>
          {autoPromoteEnabled && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <Label className="text-xs">Consecutive approvals before suggestion</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={autoPromoteThreshold} onChange={e => setAutoPromoteThreshold(e.target.value)} className="h-8 text-xs w-20" min={5} max={50} disabled={!canUpdate} />
                  <span className="text-[10px] text-muted-foreground">approvals in a row</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Maximum risk score for auto-promote</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={autoPromoteMaxRisk} onChange={e => setAutoPromoteMaxRisk(e.target.value)} className="h-8 text-xs w-20" min={10} max={70} disabled={!canUpdate} />
                  <span className="text-[10px] text-muted-foreground">% — tools above this risk level can never be auto-promoted</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium">Require Admin Review</p>
                  <p className="text-[10px] text-muted-foreground">Admin must confirm before a tool is actually auto-promoted</p>
                </div>
                <Switch checked={autoPromoteRequireReview} onCheckedChange={setAutoPromoteRequireReview} disabled={!canUpdate} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Routing Rules */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Approval Routing Rules</CardTitle>
          </div>
          <CardDescription className="text-xs">Automatic rules that determine approval requirements based on conditions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {routingRules.map(rule => (
            <div key={rule.id} className={cn('flex items-center gap-3 p-2.5 rounded-lg border transition-colors', rule.enabled ? 'bg-muted/20' : 'bg-muted/5 opacity-60')}>
              <Switch checked={rule.enabled} onCheckedChange={() => toggleRoutingRule(rule.id)} disabled={!canUpdate} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium">{rule.condition}</p>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <ChevronRight className="h-2.5 w-2.5" /> {rule.action}
                </p>
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="gap-1 h-7 text-xs"
            disabled={!canUpdate}
            aria-disabled={!canUpdate}
            title={
              canUpdate
                ? undefined
                : 'Approval policy settings are admin/owner-only.'
            }
            onClick={() => {
              if (!canUpdate) return;
              toast.info('Custom rule builder coming in next release');
            }}
          >
            <Plus className="h-3 w-3" /> Add Rule
          </Button>
        </CardContent>
      </Card>

      {/* Save — Bug #1103 + #1104: disabled for non-admin callers so
          the user cannot click a button that would 403. Server still
          gates the PUT, this is purely a UX affordance fix. */}
      <Button
        className="gap-1.5"
        disabled={saveSettings.isPending || !canUpdate}
        aria-disabled={!canUpdate}
        title={
          canUpdate
            ? undefined
            : 'Approval policy settings are admin/owner-only.'
        }
        data-testid="approvals-settings-save"
        onClick={() => {
          // Defensive: refuse to submit even if a future regression
          // forgets to disable the button.
          if (!canUpdate) return;
          saveSettings.mutate({
            slaWindows: { low: toNum(slaLow), medium: toNum(slaMedium), high: toNum(slaHigh), critical: toNum(slaCritical) },
            escalationRules: escalationEnabled ? escalationChain : [],
            delegation: delegateEnabled
              ? { delegateTo, startDate: delegateStartDate, endDate: delegateEndDate, scope: delegateScope, retainAccess: delegateRetainAccess }
              : {},
            notifications: { email: notifyEmail, slack: notifySlack, inApp: notifyInApp, sms: notifySMS, criticalImmediate: notifyCriticalImmediate, digestHour: toNum(notifyDigestHour, 9), reminderMinutes: toNum(notifyReminderMinutes, 15) },
            routingRules,
            progressiveAutonomy: { enabled: autoPromoteEnabled, threshold: toNum(autoPromoteThreshold), requireReview: autoPromoteRequireReview, maxRisk: toNum(autoPromoteMaxRisk) },
          }, {
            onSuccess: () => toast.success('Approval settings saved'),
            onError: (e) => toast.error('Save failed', { description: e.message }),
          });
        }}
      >
        {saveSettings.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Save className="h-3.5 w-3.5" />
        )}
        {saveSettings.isPending ? 'Saving…' : 'Save Settings'}
      </Button>
    </div>
  );
}
